import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedoAlt, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import "./css/ReanalyzeWithContextPanel.css";
import { useTranslation } from "../../../../i18n/LanguageContext";

const ReanalyzeWithContextPanel = ({
  initialContext = "",
  onSubmit,
  title,
  helperText,
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t("reanalyze.title");
  const resolvedHelperText = helperText ?? t("reanalyze.helperText");
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState(initialContext || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    setContext(initialContext || "");
    setLocalError(null);
    setSuccessMessage("");
  }, [initialContext]);

  const handleSubmit = async () => {
    if (!onSubmit) return;
    const trimmed = context?.trim() || "";
    if (!trimmed) {
      setLocalError(t("reanalyze.error.addContext"));
      return;
    }

    setLocalError(null);
    setSuccessMessage("");
    setIsSubmitting(true);
    try {
      await onSubmit(trimmed);
      setSuccessMessage(t("reanalyze.success"));
      setIsOpen(false);
      setContext("");
    } catch (error) {
      setLocalError(error?.message || t("reanalyze.error.failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reanalysis-panel">
      <div className="reanalysis-panel-header">
        <h4 className="reanalysis-title">
          <FontAwesomeIcon
            icon={faInfoCircle}
            className="fa-icon-margin-right-8"
          />
          {resolvedTitle}
        </h4>
        {!isOpen && (
          <p className="reanalysis-helper-text">{resolvedHelperText}</p>
        )}
      </div>

      {!isOpen && (
        <button
          type="button"
          className="btn btn-primary reanalysis-trigger-button"
          onClick={() => setIsOpen(true)}
          disabled={isSubmitting}
        >
          {t("reanalyze.addContext")}
        </button>
      )}

      {isOpen && (
        <div className="reanalysis-form">
          <label htmlFor="reanalysis-context" className="reanalysis-label">
            {t("reanalyze.contextLabel")}
          </label>
          <textarea
            id="reanalysis-context"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={t("reanalyze.contextPlaceholder")}
            className="reanalysis-textarea"
            aria-describedby="reanalysis-char-hint"
            rows={4}
            maxLength={2000}
          />
          <div id="reanalysis-char-hint" className="reanalysis-char-count">
            {context.length} / 2,000
          </div>
          <div className="reanalysis-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !context.trim()}
            >
              <FontAwesomeIcon
                icon={faRedoAlt}
                className="fa-icon-margin-right-8"
              />
              {isSubmitting
                ? t("reanalyze.reanalyzing")
                : t("reanalyze.reanalyze")}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setIsOpen(false);
                setContext(initialContext || "");
                setLocalError(null);
              }}
              disabled={isSubmitting}
            >
              {t("reanalyze.cancel")}
            </button>
          </div>
        </div>
      )}

      {localError && <p className="reanalysis-error-message">{localError}</p>}
      {successMessage && (
        <p className="reanalysis-success-message">{successMessage}</p>
      )}
    </div>
  );
};

export default ReanalyzeWithContextPanel;
